import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { easeInOut } from '@config/animations.config';
import { IAccessoriesCarouselCard } from '@models/IAccessoriesCarouselCard';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { motion } from 'framer-motion';
import { useCallback, useState } from 'react';

const AccessoriesCarouselCard = ({
  name,
  thumbnail,
  buyNowLink,
  trackingData,
}: IAccessoriesCarouselCard) => {
  const [isHovered, setHover] = useState(false);
  const [ctaBoxHeight, setCtaBoxHeight] = useState(0);

  const ctaBoxRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setCtaBoxHeight(node.clientHeight ?? 0);
    }
  }, []);

  const handleContainerClick = () => {
    // Have to attach onClick handler to div to avoid nested anchors.
    if (buyNowLink?.url && typeof window !== 'undefined')
      window.location.href = buyNowLink.url;
  };

  const isBelowLargeDesktop = useMediaQuery(1440);

  const ctaVariants = {
    hover: { opacity: 1 },
    initial: { opacity: isBelowLargeDesktop ? 1 : 0 },
  };

  const nameVariants = {
    hover: { y: -ctaBoxHeight },
    initial: { y: 0 },
  };

  const imgVariants = {
    hover: { y: -13, scale: 1.1 },
    initial: { y: 0, scale: 1 },
  };

  return (
    <div
      onClick={handleContainerClick}
      role="link"
      tabIndex={0} // required to show ctas when tabbing
      aria-label={name}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      // eslint-disable-next-line react/no-unknown-property
      track-product-impression="true"
      data-product={JSON.stringify(trackingData ?? '')}
      className="pointer-events-auto relative flex h-full w-full flex-col justify-between gap-5 overflow-hidden rounded-[10px] bg-grey-100 px-7 py-8 dark:bg-grey-600 lg:py-10"
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleContainerClick();
      }}
    >
      <motion.div
        initial="initial"
        animate={isHovered && !isBelowLargeDesktop ? 'hover' : 'initial'}
        variants={imgVariants}
        transition={easeInOut}
      >
        {thumbnail && (
          <Image
            image={thumbnail}
            imageClasses="w-full h-full object-contain max-h-[200px] pb-5"
          />
        )}
      </motion.div>
      <motion.div
        initial="initial"
        animate={isHovered && !isBelowLargeDesktop ? 'hover' : 'initial'}
        variants={nameVariants}
        transition={easeInOut}
        className="relative flex w-full flex-col items-center justify-center text-center"
      >
        {name && (
          <p className="body-one text-center text-black-100 dark:text-white">
            {name}
          </p>
        )}
        <div className="top-full flex w-full justify-center pt-5 xl:absolute">
          <motion.div
            initial="initial"
            animate={isHovered && !isBelowLargeDesktop ? 'hover' : 'initial'}
            transition={{ duration: 0.2 }}
            variants={ctaVariants}
            ref={ctaBoxRef}
          >
            {buyNowLink?.url && buyNowLink?.name && (
              <Button
                link={buyNowLink}
                linkCallback={() => {
                  if (!trackingData) return;

                  updateDataLayer({
                    event: 'selectItem',
                    ecommerce: {
                      currency: trackingData.currency,
                      items: [mapToNewTrackingData(trackingData)],
                    },
                  });
                }}
              />
            )}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default AccessoriesCarouselCard;
